// ==============================================
// MyPolaris — Google Drive charts
// ==============================================
//
// A skipper puts chart files in one Google Drive folder, shares it as
// "Anyone with the link", and pastes the link here. Their Scouts read the
// saved folder list from our API and list/download the files from Google
// directly — no Google sign-in on the boat. Package-gated via
// packages.google_drive_charts (same gate style as the Navigation Library:
// upsell card when the plan doesn't include it, sidebar entry hidden).
//
// Readability mandate: nothing under 14px (text-sm), primary copy 16px+.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { FolderIcon, TrashIcon, SparklesIcon, ArrowTopRightOnSquareIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import { useToastStore } from '../../store/toastStore';
import type { DriveChartFolder } from '../../types/driveFolders';

/** Pull the API's user-facing message out of an axios failure. */
export function apiErrorMessage(err: unknown, fallback: string): string {
  if (isAxiosError(err)) {
    const msg = (err.response?.data as { error?: string } | undefined)?.error;
    if (msg) return msg;
  }
  return fallback;
}

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

export function DriveChartsPage() {
  const queryClient = useQueryClient();
  const addToast = useToastStore((s) => s.addToast);
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<DriveChartFolder | null>(null);

  const { data: access, isLoading: accessLoading } = useQuery({
    queryKey: ['drive-folders-access'],
    queryFn: () => api.driveFoldersAccess(),
  });

  const { data: folders, isLoading } = useQuery({
    queryKey: ['drive-folders'],
    queryFn: () => api.driveFoldersList(),
    enabled: access?.enabled === true,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['drive-folders'] });

  const add = useMutation({
    mutationFn: (link: string) => api.driveFoldersAdd(link),
    onSuccess: (row) => {
      setUrl('');
      setError(null);
      invalidate();
      addToast({ type: 'success', title: 'Folder added', message: `“${row.name}” will appear on your Scouts` });
    },
    onError: (err) => setError(apiErrorMessage(err, 'Could not add that folder. Please try again.')),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.driveFoldersRemove(id),
    onSuccess: () => {
      setRemoveTarget(null);
      invalidate();
      addToast({ type: 'success', title: 'Folder removed' });
    },
    onError: (err) => {
      setRemoveTarget(null);
      setError(apiErrorMessage(err, 'Could not remove that folder.'));
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const link = url.trim();
    if (!link) {
      setError('Paste a Google Drive folder link first.');
      return;
    }
    setError(null);
    add.mutate(link);
  };

  // ---- gate states ----
  if (accessLoading) return <div className="p-10 text-center text-slate-400">Loading…</div>;
  if (access && !access.enabled) {
    return (
      <div className="p-6 max-w-xl mx-auto mt-10">
        <div className="bg-slate-800/60 border border-slate-700 rounded-2xl p-8 text-center">
          <SparklesIcon className="w-10 h-10 mx-auto text-amber-400 mb-3" />
          <h1 className="text-xl font-bold text-white mb-2">Google Drive charts</h1>
          <p className="text-slate-300 mb-1">Keep your chart files in a Google Drive folder and have every Scout on your account list and download them — no Google sign-in on the boat.</p>
          <p className="text-slate-400 text-sm mb-6">Google Drive charts aren’t included in your current plan.</p>
          <a href="/dashboard/billing" className="inline-block px-5 py-2.5 rounded-lg bg-amber-400 text-slate-900 font-bold hover:brightness-105">
            See plans that include it
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-3xl">
      {/* header */}
      <div>
        <h1 className="text-xl font-bold text-white flex items-center gap-2">
          <FolderIcon className="w-6 h-6 text-amber-400" /> Google Drive charts
        </h1>
        <p className="text-base text-slate-300 mt-2 leading-relaxed">
          Put your charts in one Google Drive folder, share it as <span className="text-white font-semibold">“Anyone with the link”</span>, and paste the link below.
          Your Scout then lists and downloads the charts straight from Google — no Google sign-in on the boat.
        </p>
        <p className="text-sm text-amber-300/90 mt-2 flex items-start gap-1.5">
          <ExclamationTriangleIcon className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Anyone who has this link can view the folder, so only share folders you’re happy to make public.</span>
        </p>
      </div>

      {/* add form */}
      <form onSubmit={submit} className="bg-slate-800/60 border border-slate-700 rounded-2xl p-4 sm:p-5 space-y-3" noValidate>
        <label htmlFor="drive-folder-url" className="block text-sm font-semibold text-slate-200">
          Google Drive folder link
        </label>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            id="drive-folder-url"
            type="url"
            inputMode="url"
            autoComplete="off"
            spellCheck={false}
            placeholder="https://drive.google.com/drive/folders/…"
            value={url}
            onChange={(e) => { setUrl(e.target.value); if (error) setError(null); }}
            disabled={add.isPending}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? 'drive-folder-error' : undefined}
            className="flex-1 min-w-0 rounded-lg bg-slate-900 border border-slate-600 text-white text-base px-3 py-2.5 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-400/70 disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={add.isPending}
            className="px-5 py-2.5 rounded-lg bg-amber-400 text-slate-900 text-base font-bold hover:brightness-105 disabled:opacity-60 whitespace-nowrap"
          >
            {add.isPending ? 'Checking…' : 'Add folder'}
          </button>
        </div>
        {error && (
          <p id="drive-folder-error" role="alert" className="text-sm font-medium text-red-300 bg-red-900/30 border border-red-800/60 rounded-lg px-3 py-2">
            {error}
          </p>
        )}
      </form>

      {/* saved folders */}
      <section aria-labelledby="drive-folders-heading" className="space-y-2">
        <h2 id="drive-folders-heading" className="text-base font-semibold text-slate-200">
          Saved folders{folders ? ` (${folders.length})` : ''}
        </h2>
        {isLoading ? (
          <div className="text-slate-400 text-sm py-4">Loading…</div>
        ) : !folders || folders.length === 0 ? (
          <div className="bg-slate-800/40 border border-dashed border-slate-700 rounded-2xl p-6 text-center text-slate-400 text-base">
            No folders yet. Add one above and it will show up on your Scouts.
          </div>
        ) : (
          <ul className="divide-y divide-slate-700/80 bg-slate-800/60 border border-slate-700 rounded-2xl overflow-hidden">
            {folders.map((f) => (
              <li key={f.id} className="flex items-center gap-3 px-4 py-3">
                <FolderIcon className="w-6 h-6 text-amber-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-base font-semibold text-white truncate">{f.name}</div>
                  <div className="text-sm text-slate-400">Added {fmtDate(f.created_at)}</div>
                </div>
                <a
                  href={f.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hidden sm:inline-flex items-center gap-1 text-sm text-slate-300 hover:text-white px-2 py-1.5 rounded-md hover:bg-slate-700/60"
                  title="Open in Google Drive"
                >
                  Open <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                </a>
                <button
                  type="button"
                  onClick={() => setRemoveTarget(f)}
                  className="inline-flex items-center gap-1 text-sm font-medium text-red-300 hover:text-red-200 px-2 py-1.5 rounded-md hover:bg-red-900/30"
                  aria-label={`Remove ${f.name}`}
                >
                  <TrashIcon className="w-4 h-4" /> Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* remove confirm (in-app modal — browsers suppress window.confirm) */}
      {removeTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="dialog" aria-modal="true" aria-labelledby="remove-folder-title">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-md">
            <h3 id="remove-folder-title" className="text-lg font-bold text-white mb-2">Remove “{removeTarget.name}”?</h3>
            <p className="text-base text-slate-300 mb-5">Your Scouts will stop listing charts from this folder. Nothing is deleted from Google Drive.</p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setRemoveTarget(null)}
                className="px-4 py-2 rounded-lg bg-slate-700 text-white text-base hover:bg-slate-600"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => remove.mutate(removeTarget.id)}
                disabled={remove.isPending}
                className="px-4 py-2 rounded-lg bg-red-500 text-white text-base font-bold hover:bg-red-400 disabled:opacity-60"
              >
                {remove.isPending ? 'Removing…' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
